/**
 * QBase 테넌트 접속 설정 — LiveSQL 테넌트 편입 1단계
 *
 * 검토서: qbase/docs/architecture/LIVESQL_TENANT_MIGRATION_REVIEW.md
 * 등록 SQL: qbase/backend/scratchpad/LIVESQL_register.sql (URI·파라미터명이 여기와 동기)
 *
 * ★ 폴백을 두지 않는다.
 *   qbaseSysId 를 넘기지 않으면 QBase 로그인은 QSY0000005(오픈데이터모델러)로 폴백한다.
 *   그러면 "로그인은 되는데 남의 테넌트로 붙는" 상태가 되고, 조회는 0건인데 원인이 안 보인다.
 *   차라리 기동 시점에 죽는 편이 낫다 — CRA 는 빌드 시점에 env 를 굽기 때문에
 *   .env 를 고친 뒤에는 dev 서버를 재시작해야 한다.
 */

// 스탠드얼론(Electron)은 로그인을 바이패스하고(App.js) 챌린지·참여그룹 UI 도 숨기므로
// QBase 를 아예 쓰지 않는다. 그런 빌드에서 이 설정이 없다고 앱을 죽이면
// **QBase 와 무관한 SQL 편집기까지 못 쓰게 된다**. 없으면 빈 값으로 두고,
// 혹시라도 호출이 일어나면 그때 실패하게 한다(조용한 폴백이 아니라 지연된 실패).
import { IS_STANDALONE } from './features';

function required(name) {
  const v = process.env[name];
  if (!v || !String(v).trim()) {
    if (IS_STANDALONE) return '';
    throw new Error(
      `[qbase] 환경변수 ${name} 가 설정되지 않았습니다. ` +
      `frontend/.env 에 추가한 뒤 개발 서버를 재시작하세요. ` +
      `(CRA 는 빌드 시점에 env 를 굽습니다)`
    );
  }
  return String(v).trim();
}

/** QBase 백엔드 베이스 — nginx/CRA proxy 로 같은 오리진에 얹는다 (쿠키 세션이라 오리진이 갈리면 안 된다) */
export const QBASE_API = required('REACT_APP_QBASE_API');

/** 이 앱이 속한 테넌트 시스템 — livesql_provision.sh provision 이 출력한 값 */
export const QBASE_SYS_ID = required('REACT_APP_QBASE_SYS_ID');

/**
 * 편집권 식별자. QBase 편집권은 **사용자당 1세션**이라, 같은 디바이스의 좀비 세션을
 * 승계하려면 acquire-edit 요청 본문에 deviceId 가 있어야 한다 (없으면 항상 실패한다).
 */
export const DEVICE_ID = 'livesql-web';

/** 인증·세션 엔드포인트 */
export const AUTH_API = {
  login: '/qbase/api/login',
  // 소셜 로그인. **`qbaseSysId` 를 실어야 한다** — 일반 로그인과 같은 함정이고, 여기도
  // 서버 기본값은 QSY0000005(오픈데이터모델러)다. 빠뜨리면 "로그인은 되는데 남의
  // 테넌트로 붙는" 상태가 되고, 조회가 0건인데 원인이 안 보인다.
  googleLogin: '/qbase/api/google-login',
  naverLogin: '/qbase/api/naver-login',
  logout: '/qbase/api/logout',
  session: '/qbase/api/session',
  keepSession: '/qbase/api/keep-session',
  selectSystem: '/qbase/api/session/select-system',
  acquireEdit: '/qbase/api/session/acquire-edit',
  releaseEdit: '/qbase/api/session/release-edit',
  saveUser: '/qbase/api/user/save',
  // 탈회한 계정으로 다시 가입할 때의 두 갈래. QBase 가 saveUser 응답에 withdrawn:true 를 실어
  // 보내며 이 선택을 요구한다 — 고르지 않으면 공통 계정만 생기고 로그인이 안 되는 상태로 남는다.
  restoreUser: '/qbase/api/restoreUser',
  purgeUser: '/qbase/api/purgeUser',
};

/**
 * 이메일 인증·계정 관리 — QBase 가 직접 제공하는 공통 엔드포인트.
 * 예전에는 Node 백엔드가 같은 경로를 서비스계정으로 중계했을 뿐, 구현은 처음부터 QBase 였다.
 *
 * ★ 이쪽은 **공통** 저장소(VW_EML_USER·VW_USER)를 바꾼다. 로그인이 보는 **테넌트**
 *   저장소(VW_QBASE_USER)는 따로다. 그 동기화는 **서버가 한다**
 *   (EmailVerifService.syncTenantPassword / syncTenantNickname / syncTenantWithdraw).
 *   화면에서 따로 맞추지 말 것 — 임시 비밀번호 발급처럼 **세션이 없는 경로**에서는
 *   클라이언트가 아예 부를 수 없어, 화면마다 챙기는 방식은 반드시 한 곳이 빠진다.
 */
export const EMAIL_VERIF_API = {
  sendVerifCode: '/api/emailverif/send-verif-code',
  verifyCode: '/api/emailverif/verify-code',
  issueTempPassword: '/api/emailverif/issue-temp-password',
  changePassword: '/api/emailverif/change-password',
  changeNickname: '/api/emailverif/change-nickname',
  withdraw: '/api/emailverif/withdraw',
};

/** 챌린지 조회 API — LIVESQL_register.sql §1 의 uri_cn 과 정확히 일치해야 한다 */
export const LIVESQL_API = {
  challenges: '/qbase/livesql/challenges',
  challengeQuiz: '/qbase/livesql/challengeQuiz',
  challengeQuizPtcp: '/qbase/livesql/challengeQuizPtcp',
  // participantGroups(전체 그룹 + 초대코드)는 쓰지 않는다(2026-10-07) — 가입은 joinChallengeGroup, 목록은 challengeGroups(코드 없음)·myChallengeGroups
  invitedChallenges: '/qbase/livesql/invitedChallenges',
  // 챌린지 제출(2026-10-06) — 머리 + 문항을 한 번에. 내용(새 등록·문항·수준·종류)이 바뀌면 QBase 가 공용 LLM(DeepSeek)으로
  // 적정성을 판정하고 통과한 것만 저장한다(pass · notRequired · fix(fixToken) · reject). 작성자 통제·작성중 숨김도 QBase 가 건다.
  // 옛 saveChallenge · saveChallengeQuizzes 는 판정을 건너뛰는 길이라 LiveSQL 이 이것으로 바꾼 뒤 QBase 가 닫는다
  submitChallenge: '/qbase/livesql/submitChallenge',
  // 판정 기준(시스템 프롬프트 전문) — 본인 LLM 「AI 검토」 가 같은 기준으로 보게 그대로 싣는다
  challengeReviewCriteria: '/qbase/livesql/challengeReviewCriteria',
  // 공개 범위·참여그룹·좋아요·목록 집계(QBase 2026-10-07). 보이는 챌린지는 **서버가** 정한다(작성자·LSADMIN·ALL·GROUP 구성원·USER 지정)
  challengeSummary: '/qbase/livesql/challengeSummary',
  toggleChallengeLike: '/qbase/livesql/toggleChallengeLike',
  setChallengeScope: '/qbase/livesql/setChallengeScope',
  joinChallengeGroup: '/qbase/livesql/joinChallengeGroup',
  leaveChallengeGroup: '/qbase/livesql/leaveChallengeGroup',
  myChallengeGroups: '/qbase/livesql/myChallengeGroups',
  // 공개 대상 조회(작성자·LSADMIN) · 그룹 목록(초대코드 없음 — 가입할 그룹 고르기용)
  challengeScope: '/qbase/livesql/challengeScope',
  challengeGroups: '/qbase/livesql/challengeGroups',
};

/** 아직 Java 실코드가 담당하는 경로 (저장 1종 + 첨부 다운로드) */
export const LIVESQL_LEGACY_API = {
  saveQuizParticipation: '/livesql/saveQryChallengeQuizPtcp',
  downloadQuizFile: '/livesql/downloadQuizFile',
};

/**
 * 한글 쿼리 파라미터명 — LIVESQL_register.sql 의 [placeholder] 와 문자 단위로 같아야 한다.
 * 다르면 치환이 일어나지 않고, 미치환 절은 통째로 제거되므로 **에러 없이 전건이 조회된다.**
 */
export const QUERY_PARAM = {
  challengeLevel: '챌린지난이도코드',
  challengeId: '쿼리챌린지ID',
  groupId: '참여그룹ID',
};
